import {
	FC,
	ReactNode,
	createContext,
	useContext,
	useMemo,
	useState,
} from "react";

import { StorageKeys } from "@/storage/storageKeys";
import { useTheme } from "@mui/material/styles";

export type ChecklistFontScale = "small" | "medium" | "large";

interface ChecklistViewContextValue {
	fontScale: ChecklistFontScale;
	setFontScale: (scale: ChecklistFontScale) => void;
	/** Base font size multiplier for body text (e.g. 0.9, 1, 1.15) */
	fontSizeMultiplier: number;
	/** CSS font-size for body content — derived from theme typography and checklist zoom */
	bodyFontSize: string;
	/** Font size for headings in checklist (e.g. step number, section headers) — derived from theme */
	headingFontSize: string;
	/** When true, checklist header (title + ingress) is collapsed to save space; progress bar stays visible */
	headerCollapsed: boolean;
	setHeaderCollapsed: (collapsed: boolean) => void;
}

const defaultScale: ChecklistFontScale = "medium";

const fontScaleConfig: Record<ChecklistFontScale, { multiplier: number }> = {
	small: { multiplier: 0.9 },
	medium: { multiplier: 1 },
	large: { multiplier: 1.15 },
};

const ChecklistViewContext = createContext<ChecklistViewContextValue | null>(
	null,
);

function loadStoredScale(): ChecklistFontScale {
	if (typeof window === "undefined") return defaultScale;
	try {
		const stored = localStorage.getItem(StorageKeys.checklistFontScale);
		if (stored === "small" || stored === "medium" || stored === "large")
			return stored;
	} catch {
		// ignore
	}
	return defaultScale;
}

function saveScale(scale: ChecklistFontScale) {
	if (typeof window === "undefined") return;
	try {
		localStorage.setItem(StorageKeys.checklistFontScale, scale);
	} catch {
		// ignore
	}
}

interface ChecklistViewProviderProps {
	children: ReactNode;
}

export const ChecklistViewProvider: FC<ChecklistViewProviderProps> = ({
	children,
}) => {
	const theme = useTheme();
	const [fontScale, setFontScaleState] =
		useState<ChecklistFontScale>(loadStoredScale);

	const setFontScale = useMemo(
		() => (scale: ChecklistFontScale) => {
			setFontScaleState(scale);
			saveScale(scale);
		},
		[],
	);

	const setHeaderCollapsed = useMemo(
		() => (_collapsed: boolean) => {},
		[],
	);

	const config = fontScaleConfig[fontScale];
	const bodyBase = Number(theme.typography?.body2?.fontSize) || 14;
	const headingBase = Number(theme.typography?.body1?.fontSize) || 16;
	const value = useMemo<ChecklistViewContextValue>(
		() => ({
			fontScale,
			setFontScale,
			fontSizeMultiplier: config.multiplier,
			bodyFontSize: `${Math.round(bodyBase * config.multiplier)}px`,
			headingFontSize: `${Math.round(headingBase * config.multiplier)}px`,
			headerCollapsed: false,
			setHeaderCollapsed,
		}),
		[
			fontScale,
			setFontScale,
			config.multiplier,
			bodyBase,
			headingBase,
			setHeaderCollapsed,
		],
	);

	return (
		<ChecklistViewContext.Provider value={value}>
			{children}
		</ChecklistViewContext.Provider>
	);
};

export function useChecklistView(): ChecklistViewContextValue {
	const ctx = useContext(ChecklistViewContext);
	if (!ctx) {
		return {
			fontScale: defaultScale,
			setFontScale: () => {},
			fontSizeMultiplier: 1,
			bodyFontSize: "1rem",
			headingFontSize: "1rem",
			headerCollapsed: false,
			setHeaderCollapsed: () => {},
		};
	}
	return ctx;
}
